import { useEffect, useState } from "react";
import axios from "axios";
import { useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { RootState } from "../../store/Index";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import { tokens } from "../../theme";
import apiService from "../../service/Api";
import { showErrorToast } from "../../components/common/ShowToast";
import ApplicationTopBar from "./components/ApplicationTopBar";
import ConferenceBroadcastPanel from "./components/ConferenceBroadcastPanel";
import OrganiserPanel from "./components/OrganiserPanel";
import VenueInfrastructurePanel from "./components/VenueInfrastructurePanel";
import ConvertToBookingDialog from "./components/QuoteConverttoBookingDialog";
import EvaluationPanel from "./components/EvaluationPanel";

export type ApplicationStatus =
  | "draft"
  | "submitted"
  | "under_review"
  | "approved"
  | "rejected";

export interface ApiOrganiser {
  lead_name: string;
  org_name: string;
  email: string | null;
  phone_number: string | null;
  experience: string;
  contact: number | null;
}

export interface ApiAudience {
  slug: string;
  label: string;
}

export interface ApiEvaluation {
  id: number;
  application: number;
  stage: string;
  scores: Record<string, number>;
  criteria: Record<string, string>;
  comments: string;
  risks: string;
  overall_score: number | null;
  is_complete: boolean;
  evaluated_by: { id: number; name: string; email: string } | null;
  evaluated_at: string;
}

export interface ApiApplication {
  id: number;
  reference_id: string;
  status: ApplicationStatus;
  created_at: string;
  organiser: ApiOrganiser;
  conference_title: string;
  conference_start_date: string;
  conference_end_date: string | null;
  conference_time: string | null;
  attendees: number | null;
  theme: string;
  local_impact: string;
  venue_name: string;
  venue_latitude: string | null;
  venue_longitude: string | null;
  capacity: number | null;
  event_flyer: string | null;
  audiences: (string | ApiAudience)[];
  amenities: (string | ApiAudience)[];
  status_changed_by: {
    id: number;
    name: string;
    email: string;
  } | null;
  status_changed_at: string | null;
  evaluations: ApiEvaluation[];
  converted_booking: { id: number; reference_number: string | null } | null;
}

export const getStatusStyles = (status: ApplicationStatus) => {
  switch (status) {
    case "submitted":
      return { bgcolor: "#F79E1B22", color: "#F79E1B" };
    case "approved":
      return { bgcolor: "#4BA66322", color: "#4BA663" };
    case "under_review":
      return { bgcolor: "#3B82F622", color: "#3B82F6" };
    case "rejected":
      return { bgcolor: "#EB433522", color: "#EB4335" };
    default:
      return { bgcolor: "#E5E7EB", color: "#6B7280" };
  }
};

export const STATUS_LABELS: Record<ApplicationStatus, string> = {
  draft: "Draft",
  submitted: "Submitted",
  under_review: "In Review",
  approved: "Approved",
  rejected: "Rejected",
};

const declineReasons = [
  {
    label: "Not enough reach",
    message:
      "Thank you for applying. Unfortunately your event does not meet our minimum broadcast reach requirements at this time.",
  },
  {
    label: "Infrastructure gap",
    message:
      "Thank you for your application. We are unable to proceed as the venue does not meet our broadcast infrastructure requirements.",
  },
  {
    label: "Scheduling conflict",
    message:
      "Thank you for applying. Unfortunately we are unable to accommodate your event date within our current production schedule.",
  },
];

const modalContent: Record<
  ApplicationStatus,
  { title: string; subtitle: string }
> = {
  rejected: {
    title: "Decline Broadcast Application",
    subtitle:
      "Select a reason template and customise the message sent to the applicant.",
  },
  under_review: {
    title: "Request Additional Information",
    subtitle:
      "Send a WhatsApp message requesting more details about the event or venue.",
  },
  approved: {
    title: "Approve Broadcast Application",
    subtitle:
      "Confirm that this event will receive sponsored livestream coverage.",
  },
  submitted: { title: "", subtitle: "" },
  draft: { title: "", subtitle: "" },
};

// Mirrors backend/streamfund/signals.py INFO_REQUEST_CATEGORIES -- kept in
// sync by hand. `missing` decides which are pre-checked when the modal opens.
const buildInfoRequestCategories = (app: ApiApplication | null) => [
  {
    key: "attendees",
    label: "Updated Attendee Count",
    missing: !app?.attendees,
  },
  {
    key: "venue",
    label: "Venue Readiness Confirmation",
    missing: !app?.venue_name || !app?.capacity || (app?.amenities?.length ?? 0) === 0,
  },
  {
    key: "program",
    label: "Event Program & Impact",
    missing: !app?.theme || !app?.local_impact || !app?.conference_time,
  },
  {
    key: "documents",
    label: "Supporting Documents",
    missing: !app?.event_flyer,
  },
  {
    key: "audience",
    label: "Target Audience",
    missing: (app?.audiences?.length ?? 0) === 0,
  },
];

export default function StreamFundApplicationDetail() {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const navigate = useNavigate();
  const { id: referenceId } = useParams<{ id: string }>();

  const authUser = useSelector((state: RootState) => state.auth.user);
  const isAdmin = !!authUser?.group_names?.includes("Admin");
  const isOperations = !!authUser?.group_names?.includes("Operations") || isAdmin;
  const isFinance = !!authUser?.group_names?.includes("Finance") || isAdmin;

  const cardBg = colors.primary[900];
  const mutedCard = colors.primary[800];
  const textPrimary = colors.primary[700];
  const textMuted = colors.primary[300];

  const [apiApp, setApiApp] = useState<ApiApplication | null>(null);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState<ApplicationStatus>("submitted");

  const [openModal, setOpenModal] = useState(false);
  const [selectedAction, setSelectedAction] =
    useState<ApplicationStatus | null>(null);
  const [reason, setReason] = useState(declineReasons[0].label);
  const [message, setMessage] = useState("");
  const [infoRequestCategories, setInfoRequestCategories] = useState<string[]>([]);
  const [savingStatus, setSavingStatus] = useState(false);

  const [convertOpen, setConvertOpen] = useState(false);
  const convertedBooking = apiApp?.converted_booking ?? null;
  const canConvert = status === "approved" && !convertedBooking;
  const adminEvaluationComplete =
    apiApp?.evaluations.find((e) => e.stage === "admin")?.is_complete ?? false;

  useEffect(() => {
    if (!referenceId) return;
    setLoading(true);
    apiService
      .get<ApiApplication>(`/api/streamfund/applications/${referenceId}/`)
      .then((res) => {
        setApiApp(res.data);
        setStatus(res.data.status);
      })
      .catch(() => setApiApp(null))
      .finally(() => setLoading(false));
  }, [referenceId]);

  const handleStatusChange = (value: ApplicationStatus) => {
    if (value === "submitted" || value === "draft") return;
    setSelectedAction(value);

    const name = apiApp?.organiser.lead_name;
    const refId = apiApp?.reference_id;
    const title = apiApp?.conference_title;
    const rawDate = apiApp?.conference_start_date;
    const rawEndDate = apiApp?.conference_end_date;
    const formatEventDate = (value: string) =>
      new Date(value)
        .toLocaleDateString("en-GB", {
          weekday: "long",
          day: "numeric",
          month: "long",
          year: "numeric",
          timeZone: "UTC",
        })
        .replace(/,/g, "");
    const date = rawDate
      ? rawEndDate
        ? `${formatEventDate(rawDate)} to ${formatEventDate(rawEndDate)}`
        : formatEventDate(rawDate)
      : "";

    if (value === "rejected") {
      setReason(declineReasons[0].label);
      setMessage(
        `Dear ${name},\n\nThank you for your interest in the Muzukuru StreamFund programme.\n\nAfter careful review of your application (${refId}) for ${title}, we regret to inform you that we are unable to approve it at this time.\n\nWe appreciate the effort you put into your application and encourage you to apply again in the future.\n\nBest regards,\nThe Livestream Company Team`,
      );
    } else if (value === "under_review") {
      setMessage(
        `Dear ${name},\n\nThank you for submitting your StreamFund application (${refId}) for ${title}.\n\nWe have reviewed your submission and require some additional information before we can proceed. A member of our team will contact you shortly.\n\nBest regards,\nThe Livestream Company Team`,
      );
      setInfoRequestCategories(
        buildInfoRequestCategories(apiApp)
          .filter((c) => c.missing)
          .map((c) => c.key),
      );
    } else if (value === "approved") {
      setMessage(
        `Dear ${name},\n\nWe are thrilled to inform you that your StreamFund application (${refId}) for ${title} scheduled on ${date} has been approved.\n\nThank you for partnering with us.\n\nBest regards,\nThe Livestream Company Team`,
      );
    }

    setOpenModal(true);
  };

  const handleSaveEvaluation = async (
    stage: "operations" | "finance" | "admin",
    payload: {
      scores: Record<string, number>;
      comments: string;
      risks: string;
    },
  ) => {
    if (!referenceId) return;
    const res = await apiService.put<ApiEvaluation>(
      `/api/streamfund/applications/${referenceId}/evaluations/${stage}/`,
      payload,
    );
    setApiApp((prev) => {
      if (!prev) return prev;
      const evaluations = prev.evaluations.filter((e) => e.stage !== stage);
      return { ...prev, evaluations: [...evaluations, res.data] };
    });
    return res.data; // let EvaluationPanel read the server-computed is_complete
  };

  const handleFlyerUpload = async (file: File) => {
    if (!referenceId) return;
    const formData = new FormData();
    formData.append("event_flyer", file);
    const res = await apiService.patch<ApiApplication>(
      `/api/streamfund/applications/${referenceId}/`,
      formData,
    );
    setApiApp(res.data);
  };

  const handleConfirm = async () => {
    if (!selectedAction || !referenceId) return;
    setSavingStatus(true);
    try {
      const res = await apiService.patch<ApiApplication>(
        `/api/streamfund/applications/${referenceId}/`,
        {
          status: selectedAction,
          ...(selectedAction === "under_review"
            ? { info_request_categories: infoRequestCategories }
            : {}),
        },
      );
      setApiApp(res.data);
      setStatus(res.data.status);
      setOpenModal(false);

      const phone = apiApp?.organiser.phone_number?.replace(/\D/g, "");
      if (phone && message) {
        const encoded = encodeURIComponent(message);
        window.open(`https://wa.me/${phone}?text=${encoded}`, "_blank");
      }
    } catch (err) {
      const data = axios.isAxiosError(err)
        ? (err.response?.data as unknown)
        : undefined;
      const detail = Array.isArray(data)
        ? data[0]
        : (data as { detail?: string; status?: string[] } | undefined)?.detail ??
          (data as { detail?: string; status?: string[] } | undefined)?.status?.[0];
      showErrorToast(detail || "Something went wrong. Please try again.");
    } finally {
      setSavingStatus(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!apiApp) {
    return (
      <Box sx={{ py: 6, textAlign: "center" }}>
        <Typography sx={{ color: textMuted, fontSize: 14 }}>
          Application not found.
        </Typography>
      </Box>
    );
  }

  const audienceLabels = (apiApp.audiences ?? []).map((a) =>
    typeof a === "string" ? a : a.label,
  );
  const amenitySlugs = (apiApp.amenities ?? []).map((a) =>
    typeof a === "string" ? a : a.slug,
  );

  const app = {
    id: apiApp.reference_id,
    status: apiApp.status,
    submitted: apiApp.created_at,
    organiser: {
      leadName: apiApp.organiser.lead_name,
      orgName: apiApp.organiser.org_name || "—",
      email: apiApp.organiser.email || "—",
      phoneNumber: apiApp.organiser.phone_number || "—",
      experience: apiApp.organiser.experience || "Not provided",
    },
    conference: {
      title: apiApp.conference_title,
      theme: apiApp.theme || "Not provided",
      date: apiApp.conference_start_date,
      endDate: apiApp.conference_end_date,
      time: apiApp.conference_time,
      attendees: apiApp.attendees ?? 0,
      localImpact: apiApp.local_impact || "Not provided",
      categories: audienceLabels,
    },
    venue: {
      venueName: apiApp.venue_name,
      capacity: apiApp.capacity ?? 0,
      amenities: amenitySlugs,
      location: {
        latitude: apiApp.venue_latitude
          ? Number(apiApp.venue_latitude)
          : -17.8252,
        longitude: apiApp.venue_longitude
          ? Number(apiApp.venue_longitude)
          : 31.0335,
      },
    },
  };

  return (
    <Box sx={{ minHeight: "100vh", display: "flex", flexDirection: "column" }}>
      <Box sx={{ flexGrow: 1 }}>
        <ApplicationTopBar
          status={status}
          onBack={() => navigate(-1)}
          onStatusChange={handleStatusChange}
          onConvert={() => setConvertOpen(true)}
          convertDisabled={!canConvert}
          convertedBooking={convertedBooking}
          approveDisabled={!adminEvaluationComplete}
          isAdmin={isAdmin}
        />

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              md: "1fr 1fr",
              lg: "350px 1fr 360px",
            },
            gap: 3,
            alignItems: "stretch",
          }}
        >
          {/* LEFT PANEL */}
          <OrganiserPanel
            organiser={app.organiser}
            contactId={apiApp.organiser.contact}
            appInfo={{ id: app.id, submitted: app.submitted }}
            eventFlyer={apiApp.event_flyer}
            onFlyerUpload={handleFlyerUpload}
          />

          {/* MIDDLE PANEL */}
          <Stack
            spacing={2.5}
            sx={{ height: "100%", display: "flex", flexDirection: "column" }}
          >
            <ConferenceBroadcastPanel
              conference={app.conference}
              venueName={app.venue.venueName}
            />
            <VenueInfrastructurePanel venue={app.venue} />
          </Stack>

          {/* RIGHT PANEL */}
          <EvaluationPanel
            referenceId={apiApp.reference_id}
            conferenceDate={apiApp.conference_start_date}
            status={status}
            onStatusChange={handleStatusChange}
            onConvert={() => setConvertOpen(true)}
            convertedBooking={convertedBooking}
            isAdmin={isAdmin}
            isOperations={isOperations}
            isFinance={isFinance}
            evaluations={apiApp.evaluations}
            onSaveEvaluation={handleSaveEvaluation}
          />
        </Box>
      </Box>

      {apiApp.status_changed_by &&
        (status === "approved" || status === "rejected") && (
          <Box
            sx={{
              mt: 4,
              px: 2,
              py: 3,
              bgcolor: colors.primary[900],
              borderRadius: "16px",
              border: "none",
              display: "flex",
              justifyContent: "flex-end",
              alignItems: "center",
            }}
          >
            <Typography
              sx={{
                fontSize: 12,
                color: colors.primary[700],
                fontWeight: 500,
              }}
            >
              {status === "approved" ? "Approved" : "Rejected"} by{" "}
              <Box
                component="span"
                sx={{ fontWeight: 700, color: colors.primary[400] }}
              >
                {apiApp.status_changed_by.name}
              </Box>
              {apiApp.status_changed_at && (
                <>
                  {" on "}
                  <Box
                    component="span"
                    sx={{ fontWeight: 600, color: colors.primary[700] }}
                  >
                    {new Date(apiApp.status_changed_at).toLocaleDateString(
                      undefined,
                      {
                        dateStyle: "medium",
                      },
                    )}
                  </Box>
                </>
              )}
            </Typography>
          </Box>
        )}

      {/* ACTION MODAL */}
      <Dialog
        open={openModal}
        onClose={() => setOpenModal(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogContent sx={{ p: 3, bgcolor: cardBg }}>
          {selectedAction &&
            selectedAction !== "submitted" &&
            selectedAction !== "draft" && (
              <>
                <Typography
                  sx={{
                    fontSize: 22,
                    fontWeight: 700,
                    color: textPrimary,
                    mb: 0.75,
                  }}
                >
                  {modalContent[selectedAction].title}
                </Typography>
                <Typography sx={{ color: textMuted, mb: 3, fontSize: 14 }}>
                  {modalContent[selectedAction].subtitle}
                </Typography>
              </>
            )}

          {selectedAction === "rejected" && (
            <Box sx={{ mb: 2 }}>
              <Typography
                sx={{
                  mb: 0.75,
                  fontSize: 12,
                  fontWeight: 600,
                  color: textMuted,
                }}
              >
                Reason
              </Typography>
              <Select
                fullWidth
                value={reason}
                onChange={(e) => {
                  const selected = declineReasons.find(
                    (x) => x.label === e.target.value,
                  );
                  setReason(e.target.value);
                  if (selected) setMessage(selected.message);
                }}
                sx={{
                  height: 40,
                  borderRadius: "8px",
                  fontSize: 13,
                  color: textPrimary,
                  bgcolor: mutedCard,
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: `${textMuted}30`,
                  },
                }}
              >
                {declineReasons.map((r) => (
                  <MenuItem key={r.label} value={r.label} sx={{ fontSize: 13 }}>
                    {r.label}
                  </MenuItem>
                ))}
              </Select>
            </Box>
          )}

          {selectedAction === "under_review" && (
            <Box sx={{ mb: 2 }}>
              <Typography
                sx={{
                  mb: 0.75,
                  fontSize: 12,
                  fontWeight: 600,
                  color: textMuted,
                }}
              >
                Information Requested (shown in the email and response portal)
              </Typography>
              <Stack spacing={0.5}>
                {buildInfoRequestCategories(apiApp).map((cat) => {
                  const checked = infoRequestCategories.includes(cat.key);
                  return (
                    <Box
                      key={cat.key}
                      onClick={() =>
                        setInfoRequestCategories((prev) =>
                          prev.includes(cat.key)
                            ? prev.filter((k) => k !== cat.key)
                            : [...prev, cat.key],
                        )
                      }
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        px: 1.25,
                        py: 0.75,
                        borderRadius: "8px",
                        cursor: "pointer",
                        bgcolor: checked ? `${textMuted}14` : "transparent",
                        "&:hover": { bgcolor: `${textMuted}14` },
                      }}
                    >
                      <Box
                        sx={{
                          width: 16,
                          height: 16,
                          borderRadius: "4px",
                          border: `1.5px solid ${checked ? "#3B82F6" : textMuted}`,
                          bgcolor: checked ? "#3B82F6" : "transparent",
                          flexShrink: 0,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          color: "#fff",
                          fontSize: 11,
                        }}
                      >
                        {checked ? "✓" : ""}
                      </Box>
                      <Typography sx={{ fontSize: 13, color: textPrimary }}>
                        {cat.label}
                      </Typography>
                      {cat.missing && (
                        <Typography
                          sx={{ fontSize: 10, color: "#F79E1B", ml: "auto" }}
                        >
                          MISSING
                        </Typography>
                      )}
                    </Box>
                  );
                })}
              </Stack>
            </Box>
          )}

          <Box>
            <Typography
              sx={{ mb: 0.75, fontSize: 12, fontWeight: 600, color: textMuted }}
            >
              Message
            </Typography>
            <TextField
              fullWidth
              multiline
              minRows={4}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Write the WhatsApp message to send to the applicant"
              sx={{
                "& .MuiOutlinedInput-root": {
                  alignItems: "flex-start",
                  borderRadius: "8px",
                  bgcolor: mutedCard,
                  fontSize: 13,
                  color: textPrimary,
                  "& fieldset": { borderColor: `${textMuted}30` },
                  "&:hover fieldset": { borderColor: `${textMuted}55` },
                },
                "& .MuiOutlinedInput-input": { padding: "12px", fontSize: 13 },
              }}
            />
          </Box>

          <Box display="flex" justifyContent="flex-end" gap={1.5} mt={3}>
            <Button
              onClick={() => setOpenModal(false)}
              sx={{ color: textMuted, textTransform: "none" }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleConfirm}
              disabled={savingStatus}
              sx={{
                textTransform: "none",
                borderRadius: "8px",
                fontWeight: 600,
                bgcolor:
                  selectedAction === "approved"
                    ? "#4BA663"
                    : selectedAction === "rejected"
                      ? "#EB4335"
                      : "#3B82F6",
                "&:hover": { opacity: 0.9 },
              }}
            >
              {savingStatus
                ? "Saving…"
                : selectedAction === "rejected"
                  ? "Confirm Decline"
                  : selectedAction === "approved"
                    ? "Confirm Approval"
                    : "Send Request"}
            </Button>
          </Box>
        </DialogContent>
      </Dialog>

      <ConvertToBookingDialog
        open={convertOpen}
        onClose={() => setConvertOpen(false)}
        quoteId={referenceId!}
        convertEndpoint={`/api/streamfund/applications/${referenceId}/convert-to-booking/`}
        venueName={apiApp.venue_name}
      />
    </Box>
  );
}
